<template>
  <PVButtonGroup
    ref="buttonRef"
    :data-testid="dataTestid"
    :disabled="isButtonBusyOrDisabled"
    :pt="passThroughProps"
  >
    <slot />
  </PVButtonGroup>
</template>

<script lang="ts">
import { PassThrough } from '@primevue/core';
import type { ButtonGroupPassThroughOptions } from 'primevue/buttongroup';
import { ref, computed, defineComponent } from 'vue';

const STANDARD_BUTTON_GROUP_ROOT_CLASS = 'standard-button-group';

export default defineComponent({
  name: 'StandardButtonGroup',
  compatConfig: {
    MODE: 3,
  },
  props: {
    disabled: {
      type: Boolean,
    },
    dataTestid: {
      type: String,
    },
    /**
     * If true, the group spans the full width of its container.
     */
    fluid: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const buttonRef = ref<{ $el: HTMLElement } | null>(null);

    const isButtonBusyOrDisabled = computed(() => props.disabled);

    /**
     * ButtonGroup sets inheritAttrs: false; pass-through attaches layout classes to the root
     * span that wraps slot children (required for direct-child seam selectors in scoped CSS).
     * https://primevue.org/passthrough/
     */
    const passThroughProps = computed((): PassThrough<ButtonGroupPassThroughOptions> => {
      return {
        root: {
          class: [
            STANDARD_BUTTON_GROUP_ROOT_CLASS,
            props.fluid ? `${STANDARD_BUTTON_GROUP_ROOT_CLASS}--fluid` : null,
          ].filter(Boolean),
        },
      };
    });

    const focus = () => {
      buttonRef.value?.$el.focus();
    };

    return {
      buttonRef,
      isButtonBusyOrDisabled,
      passThroughProps,
      focus,
    };
  },
});
</script>

<style scoped lang="scss">
.standard-button-group {
  &--fluid {
    width: 100%;
  }

  :deep(> .standard-button) {
    position: relative;
    flex: 1 1 auto;

    &:hover {
      z-index: 1;
    }

    &:focus,
    &:focus-visible,
    &:active {
      z-index: 1;
    }

    &:not(:first-child) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    &:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}
</style>
